import React, { useState } from "react";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Container,
  Paper,
  List,
  ListItem,
  ListItemText,
  Box,
  Collapse,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const DissertationServices: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const otherServices = [
    { title: "Dissertation", route: "/dissertation-writing-services" },
    { title: "Coursework", route: "/coursework-writing" },
    { title: "Data Analytics", route: "/data-analysis-services" },
    { title: "Project writing services", route: "/project-report-writing" },
    { title: "Literature review", route: "/literature-review-writing" },
    { title: "Manuscript", route: "/manuscript-preparation" },
    { title: "Research methodology", route: "/research-methodology-services" },
  ];

  const data = {
    icon: "ds6.webp",
    title: "Publication Services",
    image: "ds6.webp",
    description:
      "Our publication services support students and researchers in preparing high-quality, publication-ready work. From structured manuscript writing to statistical analysis and journal submission guidance, we help enhance academic credibility and increase the likelihood of successful publication.",
    additionalInfo: [
      {
        title: "Research Implementation",
        description:
          "We provide expert guidance on implementing robust research methodologies, including quantitative and qualitative methods, to ensure accuracy, credibility, and academic rigor in your publications.",
        icon1: "",
        cards: [
          {
            title: "Quantitative vs. Qualitative Research",
            description:
              "Understand when to apply quantitative or qualitative methods. This section explores the advantages of each, helping you select the most appropriate approach for impactful research results.",
          },
          {
            title: "Choosing a Research Design",
            description:
              "Select the right research design—experimental, observational, or case study—to align with your publication goals and ensure comprehensive data gathering.",
          },
          {
            title: "Data Collection Techniques",
            description:
              "Learn effective techniques such as surveys and interviews for gathering credible data. Minimize bias to ensure reliable results that support publication standards.",
          },
          {
            title: "Data Analysis for Publications",
            description:
              "Master data analysis techniques like statistical and thematic analysis, crucial for interpreting research findings and strengthening the validity of your publication.",
          },
        ],
      },
      {
        title: "Academic Journal Manuscript Writing",
        description:
          "Our team assists you in crafting well-structured, publication-ready manuscripts for academic journals. We help organize content and ensure adherence to journal requirements, improving the chances of acceptance.",
        icon1: "",
        cards: [
          {
            title: "Structuring Your Manuscript",
            description:
              "Learn the key sections of a journal manuscript, from abstract to discussion, for a well-organized, publication-ready document.",
          },
          {
            title: "Citations and Referencing",
            description:
              "Maintain academic integrity by mastering citation styles like APA, MLA, and Chicago. This section ensures proper credit to sources and prevents plagiarism.",
          },
          {
            title: "Understanding the Peer Review Process",
            description:
              "Prepare for peer review by learning how to incorporate constructive feedback. This knowledge helps you refine your work to meet high academic standards.",
          },
          {
            title: "Journal Submission Guidelines",
            description:
              "Each journal has specific guidelines for submission. Learn how to tailor your manuscript to meet these standards, increasing your chances of acceptance.",
          },
        ],
      },
      {
        title: "Statistical Analysis and Data Presentation",
        description:
          "Our statistical analysis services enhance the quality of your publication by ensuring accurate data interpretation, presentation, and visualization for a compelling, credible study.",
        icon1: "",
        cards: [
          {
            title: "Effective Data Collection Methods",
            description:
              "Learn various data collection methods, from surveys to experiments, ensuring reliable data for your publication.",
          },
          {
            title: "Descriptive Statistics",
            description:
              "Summarize your data clearly using measures like mean, median, and standard deviation, helping readers grasp your findings at a glance.",
          },
          {
            title: "Inferential Statistics for Research",
            description:
              "Use inferential statistics, such as hypothesis testing and confidence intervals, to draw accurate conclusions that strengthen your publication’s impact.",
          },
          {
            title: "Data Visualization Techniques",
            description:
              "Enhance clarity with charts, graphs, and tables. Effective data visualization improves reader comprehension and makes your research more impactful.",
          },
        ],
      },
      {
        title: "Publication-Ready Journal Writing",
        description:
          "We support students and researchers in preparing manuscripts that adhere to journal standards, guiding you through the writing and submission process to increase publication success.",
        icon1: "",
        cards: [
          {
            title: "Journal Structure and Formatting",
            description:
              "Organize your manuscript with the correct sections—abstract, methods, results—for a cohesive and publication-ready submission.",
          },
          {
            title: "Citation and Referencing",
            description:
              "Proper citation practices enhance credibility and prevent plagiarism, essential for meeting academic and journal requirements.",
          },
          {
            title: "Preparing for Peer Review",
            description:
              "Understand the peer review process and learn how to implement reviewer feedback, an essential step in the publication process.",
          },
          {
            title: "Adhering to Journal Guidelines",
            description:
              "Customize your manuscript according to specific journal requirements to improve the likelihood of acceptance and successful publication.",
          },
        ],
      },
    ],
  };

  const handleServiceClick = (route: string) => {
    window.location.href = route;
  };

  const handleHeadingClick = (index: number) => {
    setActiveIndex(activeIndex === index ? 0 : index);
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Grid
        container
        spacing={0}
        sx={{
          backgroundColor: "#172554",
          padding: { sm: 0, lg: 3 },
          display: "flex",
          flexDirection: "row-reverse",
        }}
        className="component"
      >
        {/* Left Side - Other Services List (Desktop) and Expandable Headings (Mobile) */}
        <Grid item xs={12} md={3}>
          {/* Desktop View */}
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <List>
              {data.additionalInfo.map((info, index) => (
                <ListItem
                  button
                  key={index}
                  onClick={() => handleHeadingClick(index)}
                  selected={activeIndex === index}
                  sx={{ color: "white" }}
                >
                  <ListItemText primary={info.title} />
                </ListItem>
              ))}
              <Typography
                sx={{ textAlign: "center", color: "white", fontWeight: "bold" }}
              >
                Our Other Services
              </Typography>
              {otherServices.map((service, index) => (
                <ListItem
                  button
                  key={index}
                  onClick={() => handleServiceClick(service.route)}
                  sx={{ color: "white" }}
                >
                  <ListItemText primary={service.title} />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Mobile View - Expandable Headings */}
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            {data.additionalInfo.map((info, index) => (
              <Box key={index}>
                <ListItem
                  button
                  onClick={() => handleHeadingClick(index)}
                  sx={{
                    color: "white",
                    borderBottom: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <ListItemText primary={info.title} />
                  <IconButton sx={{ color: "white" }}>
                    {activeIndex === index ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </IconButton>
                </ListItem>
                <Collapse
                  in={activeIndex === index}
                  timeout="auto"
                  unmountOnExit
                >
                  <Box
                    sx={{
                      backgroundColor: "#f3f4f6",
                      color: "black",
                      padding: 2,
                    }}
                  >
                    <Typography variant="body2" sx={{ marginBottom: 2 }}>
                      {info.description}
                    </Typography>
                    {info.cards.map((card, cardIndex) => (
                      <Card
                        key={cardIndex}
                        sx={{
                          marginBottom: 2,
                          backgroundColor: "#172554",
                          color: "white",
                        }}
                      >
                        <CardContent>
                          <Typography variant="h6">
                            <i
                              className="fa-solid fa-circle-check"
                              style={{ color: "#f97316", paddingRight: "10px" }}
                            ></i>
                            {card.title}
                          </Typography>
                          <Typography variant="body2">
                            {card.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            ))}
          </Box>
        </Grid>

        {/* Right Side - Dissertation Content (Desktop Only) */}
        <Grid item md={9} sx={{ display: { xs: "none", md: "block" } }}>
          <Paper
            elevation={3}
            sx={{
              padding: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "#f3f4f6",
              boxShadow: "none",
              borderRadius: "0px",
            }}
          >
            {activeIndex !== null && (
              <>
                <Typography variant="h5" sx={{ marginBottom: 2 }}>
                  {data.additionalInfo[activeIndex].title}
                </Typography>
                <Box sx={{ marginBottom: 2 }}></Box>
                <Typography sx={{ marginBottom: 4, textAlign: "center" }}>
                  {data.additionalInfo[activeIndex].description}
                </Typography>

                <Grid
                  container
                  direction="column"
                  spacing={2}
                  sx={{
                    backgroundColor: "#172554",
                    color: "white",
                    borderRadius: "4px",
                    maxWidth: { lg: "75%", xs: "100%" },
                  }}
                >
                  {data.additionalInfo[activeIndex].cards.map((card, index) => (
                    <Grid item key={index}>
                      <Card
                        sx={{
                          boxShadow: "none",
                          backgroundColor: "transparent",
                          color: "white",
                        }}
                      >
                        <CardContent
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="h6" color={"white"}>
                            <i
                              className="fa-solid fa-circle-check"
                              style={{ color: "#f97316", paddingRight: "10px" }}
                            ></i>
                            {card.title}
                          </Typography>
                          <Typography variant="body2" textAlign={"center"}>
                            {card.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DissertationServices;
